import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BrandingForm from './components/BrandingForm';
import CalculationPanel from './components/CalculationPanel';
import BrandingItemsTable from './components/BrandingItemsTable';
import BulkActionsPanel from './components/BulkActionsPanel';

const BrandingCostCalculator = () => {
  const navigate = useNavigate();
  const [brandingItems, setBrandingItems] = useState([]);
  const [calculationData, setCalculationData] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [stats, setStats] = useState({
    totalItems: 0,
    totalCost: 0,
    plannedItems: 0,
    installedItems: 0
  });

  // Mock initial data
  useEffect(() => {
    const mockItems = [
      {
        id: 'branding-001',
        brandingId: 'WD-DHA-COC-001',
        storeId: 'store-001',
        brandName: 'coca-cola',
        brandingMedium: 'window-display',
        kvId: 'kv-001',
        description: 'Front window promotional display',
        storeDetails: { 
          value: 'store-001', 
          label: 'Dhaka Central Mall - Store A1',
          locationCode: 'DHA',
          rent: 45000, 
          size: 800 
        },
        mediumDetails: { 
          value: 'window-display', 
          label: 'Window Display',
          code: 'WD',
          baseRate: 2500 
        },
        rentPerSqFt: 56.25,
        locationMultiplier: 1.1,
        unitRate: 2750,
        totalCost: 2750,
        area: 12.5,
        status: 'installed',
        createdAt: new Date('2025-09-15T10:30:00')
      },
      {
        id: 'branding-002',
        brandingId: 'BB-CTG-CAV-002',
        storeId: 'store-002',
        brandName: 'cavotin-haircare',
        brandingMedium: 'billboard',
        kvId: 'kv-002',
        description: 'Gondola branding in Chittagong',
        storeDetails: { 
          value: 'store-002', 
          label: 'Chittagong Plaza - Store B2',
          locationCode: 'CTG',
          rent: 35000, 
          size: 650 
        },
        mediumDetails: { 
          value: 'billboard', 
          label: 'Billboard',
          code: 'BB',
          baseRate: 8000 
        },
        rentPerSqFt: 53.85,
        locationMultiplier: 1.1,
        unitRate: 8800,
        totalCost: 8800,
        area: 8.2,
        status: 'verified',
        createdAt: new Date('2025-09-20T14:15:00')
      },
      {
        id: 'branding-003',
        brandingId: 'GB-SYL-LIS-003',
        storeId: 'store-003',
        brandName: 'lily-skincare',
        brandingMedium: 'gondola-branding',
        kvId: 'kv-003',
        description: 'Product shelf branding',
        storeDetails: { 
          value: 'store-003', 
          label: 'Sylhet Shopping Center - Store C3',
          locationCode: 'SYL',
          rent: 28000, 
          size: 500 
        },
        mediumDetails: { 
          value: 'gondola-branding', 
          label: 'Gondola Branding',
          code: 'GB',
          baseRate: 3500 
        },
        rentPerSqFt: 56.0,
        locationMultiplier: 1.1,
        unitRate: 3850,
        totalCost: 3850,
        area: 15.0,
        status: 'planned',
        createdAt: new Date('2025-09-25T09:45:00')
      }
    ];

    setBrandingItems(mockItems);
  }, []);

  // Update stats when items change
  useEffect(() => {
    const totalItems = brandingItems?.length;
    const totalCost = brandingItems?.reduce((sum, item) => sum + item?.totalCost, 0);
    const plannedItems = brandingItems?.filter(item => item?.status === 'planned')?.length;
    const installedItems = brandingItems?.filter(item => item?.status === 'installed' || item?.status === 'verified')?.length;

    setStats({
      totalItems,
      totalCost,
      plannedItems,
      installedItems
    });
  }, [brandingItems]);

  const handleCalculate = (data) => {
    setIsCalculating(true);
    // Simulate calculation delay
    setTimeout(() => {
      setCalculationData(data);
      setIsCalculating(false);
    }, 500);
  };

  const handleAddItem = (item) => {
    setBrandingItems(prev => [item, ...prev]);
    setCalculationData(null);
  };

  const handleEditItem = (item) => {
    console.log('Edit item:', item);
    // Navigate to edit form or open modal
  };

  const handleDeleteItem = (itemId) => {
    setBrandingItems(prev => prev?.filter(item => item?.id !== itemId));
  };

  const handleDuplicateItem = (item) => {
    const duplicatedItem = {
      ...item,
      id: `branding-${Date.now()}`,
      createdAt: new Date(),
      status: 'planned'
    };
    setBrandingItems(prev => [duplicatedItem, ...prev]);
  };

  const handleStatusChange = (itemId, newStatus) => {
    setBrandingItems(prev => 
      prev?.map(item => 
        item?.id === itemId ? { ...item, status: newStatus } : item
      )
    );
  };

  const handleBulkCreate = (bulkData) => {
    const newItems = bulkData?.stores?.map(storeId => {
      // Mock store data lookup with location codes
      const storeDetails = {
        'store-001': { value: 'store-001', label: 'Dhaka Central Mall - Store A1', locationCode: 'DHA', rent: 45000, size: 800 },
        'store-002': { value: 'store-002', label: 'Chittagong Plaza - Store B2', locationCode: 'CTG', rent: 35000, size: 650 },
        'store-003': { value: 'store-003', label: 'Sylhet Shopping Center - Store C3', locationCode: 'SYL', rent: 28000, size: 500 },
        'store-004': { value: 'store-004', label: 'Rajshahi Market - Store D4', locationCode: 'RAJ', rent: 22000, size: 400 },
        'store-005': { value: 'store-005', label: 'Khulna Complex - Store E5', locationCode: 'KHU', rent: 30000, size: 550 }
      }?.[storeId];

      const mediumDetails = {
        'window-display': { value: 'window-display', label: 'Window Display', code: 'WD', baseRate: 2500 },
        'billboard': { value: 'billboard', label: 'Billboard', code: 'BB', baseRate: 8000 },
        'led-screen': { value: 'led-screen', label: 'LED Screen', code: 'LS', baseRate: 15000 },
        'in-store-static': { value: 'in-store-static', label: 'In-Store Static', code: 'IS', baseRate: 1200 },
        'gondola-branding': { value: 'gondola-branding', label: 'Gondola Branding', code: 'GB', baseRate: 3500 },
        'fsu': { value: 'fsu', label: 'FSU', code: 'FSU', baseRate: 5500 }
      }?.[bulkData?.brandingMedium];

      const brandDetails = {
        'coca-cola': { code: 'COC' },
        'lily-skincare': { code: 'LIS' },
        'cavotin-haircare': { code: 'CAV' },
        'samsung': { code: 'SAM' },
        'unilever': { code: 'UNI' }
      }?.[bulkData?.brandName];

      // Generate branding ID
      const serialNumber = String(Math.floor(Math.random() * 999) + 1)?.padStart(3, '0');
      const brandingId = `${mediumDetails?.code}-${storeDetails?.locationCode}-${brandDetails?.code}-${serialNumber}`;

      const rentPerSqFt = storeDetails?.rent / storeDetails?.size;
      const locationMultiplier = rentPerSqFt > 60 ? 1.3 : rentPerSqFt > 40 ? 1.1 : 1.0;
      const unitRate = Math.round(mediumDetails?.baseRate * locationMultiplier);
      const totalCost = unitRate; // No quantity multiplier

      return {
        id: `branding-${Date.now()}-${storeId}`,
        brandingId,
        storeId,
        brandName: bulkData?.brandName,
        brandingMedium: bulkData?.brandingMedium,
        kvId: bulkData?.kvId || 'kv-001',
        description: bulkData?.description,
        storeDetails,
        mediumDetails,
        brandDetails,
        rentPerSqFt,
        locationMultiplier,
        unitRate,
        totalCost,
        area: 12.5, // Mock area
        status: 'planned',
        createdAt: new Date()
      };
    });

    setBrandingItems(prev => [...newItems, ...prev]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('bn-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    })?.format(amount)?.replace('BDT', '৳');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Breadcrumbs />
        
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Brand Cost Calculator</h1>
              <p className="text-muted-foreground">
                Create and manage branding items with unique IDs and dynamic cost calculations
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkPanel(true)}
                iconName="Copy"
                iconPosition="left"
              >
                Bulk Create
              </Button>
              
              <Button
                onClick={() => navigate('/installation-tracking')}
                iconName="ArrowRight"
                iconPosition="right"
              >
                View Installations
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Package" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="text-xl font-semibold text-foreground">{stats?.totalItems}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                  <Icon name="DollarSign" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Cost</p>
                  <p className="text-xl font-semibold text-foreground">{formatCurrency(stats?.totalCost)}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Planned</p>
                  <p className="text-xl font-semibold text-foreground">{stats?.plannedItems}</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="CheckCircle" size={20} className="text-accent" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Installed</p>
                  <p className="text-xl font-semibold text-foreground">{stats?.installedItems}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="xl:col-span-2 space-y-6">
            <BrandingForm
              onSubmit={handleAddItem}
              onCalculate={handleCalculate}
              calculationData={calculationData}
              isCalculating={isCalculating}
            />
            
            <BrandingItemsTable
              items={brandingItems}
              onEdit={handleEditItem}
              onDelete={handleDeleteItem}
              onDuplicate={handleDuplicateItem}
              onStatusChange={handleStatusChange}
            />
          </div>

          {/* Calculation Panel */}
          <div className="xl:col-span-1">
            <div className="sticky top-24">
              <CalculationPanel calculationData={calculationData} />
            </div>
          </div>
        </div>
      </main>
      {/* Bulk Actions Panel */}
      <BulkActionsPanel
        isVisible={showBulkPanel}
        onClose={() => setShowBulkPanel(false)}
        onBulkCreate={handleBulkCreate}
      />
      <QuickActionButton />
    </div>
  );
};

export default BrandingCostCalculator;